/*
  Meera Capsule — visitor identification and save for later (live since 2026-10-17).

  1. Signed-in customers (including Sign in with Shop) are identified to Klaviyo on every page.
  2. Known visitors with items in the cart send a "Saved Cart" event with Requested: false
     (once per distinct cart per session). Nobody asked for these: they are for abandoned-cart
     recovery, not the "your saved cart" email.
  3. "Save for later" under the buy buttons on product pages -> "Saved Item" event.
     "Save cart" under the Capsule progress bar (cart drawer) -> "Saved Cart", Requested: true.
     Known visitors save in one tap; others get the same sheet: an optional, unticked
     "new arrivals and offers" box, then Sign in with Shop or email. The box applies to
     either path (a Shop sign-in carries it through to the next page).
  4. Empty cart drawer, signed-out visitors: the same box, Sign in with Shop, or an email
     sign-up, in place of the theme's "Log in" / "Continue shopping" buttons. The email
     path is a newsletter sign-up, so it needs the box ticked.

  Klaviyo flows: "Saved Item", "Saved Cart" (trigger filter Requested is true) and
  "Abandoned Cart" (trigger filter Requested is false).
  Saved Cart events carry CapsulePieces / CapsuleRemaining (same count as the cart's Capsule
  Builder bar: every line except Capsule and Sample products) so the emails can say how many
  more pieces unlock the gift.

  When we have the email (typed in the sheet this visit, or a signed-in customer), saves go
  straight to Klaviyo's client API, so a save the person asked for never depends on
  klaviyo.js tracking being allowed. Only cookie-recognised visitors with no email in hand
  use klaviyo.track (with a timeout, falling back to the sheet).
  The newsletter opt-in never blocks a save: if it fails, the save still completes.

  Config: window.meeraIdentify (snippets/custom-assets.liquid).
  Product data, Shop button template, styles: snippets/meera-save-item.liquid.
*/
(function () {
  'use strict';
  if (window.__meeraIdentifyInit) return;
  window.__meeraIdentifyInit = true;

  var cfg = window.meeraIdentify || {};
  var REVISION = '2024-10-15';
  var API = 'https://a.klaviyo.com/client/';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var SAVED_KEY = 'meeraSavedItems';
  var PENDING_KEY = 'meeraPendingSave';
  var CART_SAVED_KEY = 'meeraCartSaved';
  var SIGNUP_KEY = 'meeraSignedUp';
  var EMAIL_KEY = 'meeraSaveEmail';
  var CART_METRIC = 'Saved Cart';
  var CAPSULE_THRESHOLD = 3;
  var known = !!(cfg.customer && cfg.customer.email);
  var pending = {};

  /* ---------- helpers ---------- */

  function ss(key, val) {
    try {
      if (arguments.length === 1) return window.sessionStorage.getItem(key);
      if (val === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, val);
    } catch (e) { return null; }
    return null;
  }

  function withKlaviyo(cb) {
    var tries = 0;
    (function wait() {
      var k = window.klaviyo;
      if (k && typeof k.identify === 'function' && typeof k.track === 'function') { cb(k); return; }
      if (++tries > 60) return;
      setTimeout(wait, 250);
    })();
  }

  // Best-effort: also cookie this browser in klaviyo.js so later visits are recognised.
  function softIdentify(props) {
    withKlaviyo(function (k) { try { k.identify(props); } catch (e) {} });
  }

  function money(cents) { return Math.round(cents) / 100; }
  function absUrl(u) { return !u ? null : (u.indexOf('//') === 0 ? 'https:' + u : u); }
  function customerEmail() { return cfg.customer && cfg.customer.email ? cfg.customer.email : null; }
  function knownEmail() { return customerEmail() || ss(EMAIL_KEY); }
  function sourceName() { return cfg.customer ? 'signed_in' : 'known_visitor'; }

  function markKnown() {
    known = true;
    document.documentElement.classList.add('meera-known-visitor');
  }

  function reviveScripts(root) {
    root.querySelectorAll('script:not([data-meera-revived])').forEach(function (old) {
      var s = document.createElement('script');
      Array.prototype.forEach.call(old.attributes, function (a) { s.setAttribute(a.name, a.value); });
      s.text = old.text;
      s.setAttribute('data-meera-revived', '1');
      old.replaceWith(s);
    });
  }

  function apiPost(path, body) {
    return fetch(API + path + '?company_id=' + encodeURIComponent(cfg.companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error('Klaviyo ' + r.status); return r; });
  }

  // Record an event. With an email: client API (profile created/updated server-side).
  // Without one: klaviyo.track on the cookie-identified browser, giving up after 6s.
  function recordEvent(metric, props, email) {
    if (email && cfg.companyId) {
      var profile = { email: email };
      if (cfg.customer && cfg.customer.email === email) {
        if (cfg.customer.first_name) profile.first_name = cfg.customer.first_name;
        if (cfg.customer.last_name) profile.last_name = cfg.customer.last_name;
      }
      return apiPost('events/', {
        data: { type: 'event', attributes: {
          properties: props,
          value: props['$value'],
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: profile } }
        } }
      });
    }
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('klaviyo.track timeout')); }, 6000);
      withKlaviyo(function (k) {
        Promise.resolve(k.track(metric, props)).then(
          function (v) { clearTimeout(timer); resolve(v); },
          function (e) { clearTimeout(timer); reject(e); }
        );
      });
    });
  }

  // Newsletter opt-in via Klaviyo's client subscriptions endpoint. Adding the profile to the
  // single opt-in Email List is what records the consent (the client API does not accept a
  // per-profile subscriptions block). By default never throws, so a failed opt-in can't block
  // a save; strict = true lets the empty-cart sign-up (where the opt-in IS the action) report it.
  function subscribeEmail(email, source, strict) {
    if (!cfg.companyId || !cfg.listId) return strict ? Promise.reject(new Error('no list')) : Promise.resolve();
    var req = apiPost('subscriptions/', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: { data: { type: 'profile', attributes: { email: email } } }
        },
        relationships: { list: { data: { type: 'list', id: cfg.listId } } }
      }
    });
    return strict ? req : req.catch(function () { return null; });
  }

  /* ---------- 1. signed-in customers ---------- */

  function identifyCustomer() {
    var email = customerEmail();
    if (!email) return;
    markKnown();
    var props = { email: email };
    if (cfg.customer.first_name) props.first_name = cfg.customer.first_name;
    if (cfg.customer.last_name) props.last_name = cfg.customer.last_name;
    softIdentify(props);
    processPendingSave();
    if (cfg.cartCount > 0) sendSavedCart('signed_in', false, null, false).catch(function () {});
  }

  function checkKnownVisitor() {
    if (known) return;
    if (ss(EMAIL_KEY)) { markKnown(); return; }
    withKlaviyo(function (k) {
      if (typeof k.isIdentified !== 'function') return;
      Promise.resolve(k.isIdentified()).then(function (isKnown) {
        if (!isKnown) return;
        markKnown();
        if (cfg.cartCount > 0) sendSavedCart('known_visitor', false, null, false).catch(function () {});
      });
    });
  }

  /* ---------- 2. Saved Cart ---------- */

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function cartProperties(cart, source, requested) {
    var origin = window.location.origin;
    var paid = cart.items.filter(function (i) { return i.product_type !== 'Sample'; });
    var first = paid[0] || cart.items[0];
    // Capsule Builder: same count as snippets/capsule-builder-bar.liquid.
    var pieces = cart.items.reduce(function (n, i) {
      return (i.product_type === 'Capsule' || i.product_type === 'Sample') ? n : n + i.quantity;
    }, 0);
    return {
      '$value': money(cart.total_price),
      Currency: cart.currency,
      ItemCount: cart.item_count,
      ItemNames: cart.items.map(function (i) { return i.product_title; }),
      ImageURL: first ? first.image : null,
      Items: cart.items.map(function (i) {
        return {
          ProductID: i.product_id, VariantID: i.variant_id, ProductName: i.product_title,
          VariantName: i.variant_title, Quantity: i.quantity, Price: money(i.final_price),
          RowTotal: money(i.final_line_price), URL: origin + i.url, ImageURL: i.image
        };
      }),
      CartURL: origin + '/cart',
      CheckoutURL: origin + '/cart/' + paid.map(function (i) { return i.variant_id + ':' + i.quantity; }).join(','),
      CapsulePieces: pieces,
      CapsuleRemaining: Math.max(0, CAPSULE_THRESHOLD - pieces),
      Source: source,
      // true = the shopper pressed "Save cart"; false = sent quietly for a known visitor.
      Requested: requested
    };
  }

  // email: optional; defaults to the signed-in customer's or this visit's email.
  // requested: defaults to true (the shopper asked); passive identification passes false.
  function sendSavedCart(source, force, email, requested) {
    var to = email || knownEmail();
    var asked = requested !== false;
    return getCart().then(function (cart) {
      if (!cart || !cart.item_count) return false;
      var key = 'meeraSavedCart:' + (asked ? 'r:' : 'p:') + cart.items.map(function (i) { return i.variant_id + 'x' + i.quantity; }).join('|');
      if (!force && (ss(key) || pending[key])) return false;
      pending[key] = true;
      return recordEvent(CART_METRIC, cartProperties(cart, source, asked), to).then(
        function () { ss(key, '1'); delete pending[key]; return true; },
        function (err) { delete pending[key]; throw err; }
      );
    });
  }

  /* ---------- 3. Save for later (item) and Save cart ---------- */

  var ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6.5 3.5h11v17l-5.5-3.9-5.5 3.9z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>';
  var product = null, trigger = null, statusEl = null, sheet = null, lastFocus = null;
  var sheetMode = 'item', hasShop = false;

  function savedIds() {
    try { return JSON.parse(window.localStorage.getItem(SAVED_KEY) || '[]'); } catch (e) { return []; }
  }
  function rememberSaved(id) {
    try {
      var a = savedIds();
      if (a.indexOf(id) === -1) { a.push(id); window.localStorage.setItem(SAVED_KEY, JSON.stringify(a.slice(-50))); }
    } catch (e) {}
  }

  function readProduct() {
    var el = document.getElementById('MeeraSaveProduct');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function currentItem() {
    var input = document.querySelector('.product__info-container form[action*="/cart/add"] input[name="id"]') ||
                document.querySelector('form[action*="/cart/add"] input[name="id"]');
    var vid = input ? Number(input.value) : null;
    var v = product.variants.filter(function (x) { return x.id === vid; })[0] || product.variants[0];
    return {
      ProductID: product.id,
      ProductName: product.title,
      VariantID: v.id,
      VariantName: v.title === 'Default Title' ? null : v.title,
      Price: money(v.price),
      Currency: cfg.currency || null,
      URL: window.location.origin + product.url + '?variant=' + v.id,
      ImageURL: absUrl(v.image || product.image)
    };
  }

  // email: optional; defaults to the signed-in customer's or this visit's email.
  function sendSavedItem(item, source, email) {
    var props = {};
    Object.keys(item).forEach(function (key) { props[key] = item[key]; });
    props['$value'] = item.Price;
    props.Source = source;
    return recordEvent('Saved Item', props, email || knownEmail());
  }

  function showSaved(message) {
    if (!trigger) return;
    trigger.setAttribute('aria-pressed', 'true');
    trigger.querySelector('span').textContent = 'Saved';
    if (message && statusEl) { statusEl.textContent = message; statusEl.hidden = false; }
  }

  function markCartSaved() {
    ss(CART_SAVED_KEY, '1');
    document.querySelectorAll('.meera-save-bag-link button').forEach(function (b) {
      b.setAttribute('aria-pressed', 'true');
      b.querySelector('span').textContent = 'Cart saved';
    });
  }

  function wantsNews() {
    var box = sheet && sheet.querySelector('input[name="subscribe"]');
    return !!(box && box.checked);
  }

  // After Sign in with Shop, the next signed-in page view completes the save
  // (and the newsletter opt-in, if they ticked it before signing in).
  function processPendingSave() {
    var raw = ss(PENDING_KEY);
    var email = customerEmail();
    if (!raw || !email) return;
    ss(PENDING_KEY, null);
    var p;
    try { p = JSON.parse(raw); } catch (e) { return; }
    if (!p) return;
    var jobs = [];
    if (p.mode === 'cart') jobs.push(sendSavedCart('shop_sign_in', true, email));
    else if (p.mode === 'item' && p.item) jobs.push(sendSavedItem(p.item, 'shop_sign_in', email));
    if (p.subscribe) {
      var src = p.mode === 'cart' ? 'Cart: save cart (Sign in with Shop)'
        : p.mode === 'signup' ? 'Cart drawer, empty: sign up (Sign in with Shop)'
        : 'Product page: save for later (Sign in with Shop)';
      jobs.push(subscribeEmail(email, src));
    }
    Promise.all(jobs).then(function () {
      if (p.mode === 'signup') { if (p.subscribe) ss(SIGNUP_KEY, '1'); return; }
      if (p.mode === 'cart') { markCartSaved(); return; }
      if (!p.item) return;
      rememberSaved(p.item.ProductID);
      if (product && product.id === p.item.ProductID) showSaved('Saved to your account.');
    }, function () {});
  }

  // Cart drawers trap focus; release it while the sheet is open so the email field works.
  function releaseDrawerFocus() {
    if (typeof window.removeTrapFocus === 'function') { try { window.removeTrapFocus(); } catch (e) {} }
  }
  function restoreDrawerFocus() {
    var drawer = document.querySelector('cart-drawer.active, cart-drawer[open], cart-drawer .drawer.active');
    if (drawer && typeof window.trapFocus === 'function') {
      try { window.trapFocus(drawer.closest('cart-drawer') || drawer); } catch (e) {}
    }
  }

  function emailRowHtml(buttonLabel) {
    return '<div class="meera-save-sheet__row">' +
      '<label class="meera-save-sheet__field"><span class="visually-hidden">Email address</span>' +
      '<input class="meera-save-sheet__input" type="email" name="email" autocomplete="email" inputmode="email" placeholder="Email address" required></label>' +
      '<button type="submit" class="meera-save-sheet__button">' + buttonLabel + '</button>' +
    '</div>';
  }

  // Clone the Sign in with Shop button into box; onClick runs (capture phase) before Shop takes over.
  function addShopButton(box, onClick) {
    var tpl = document.getElementById('MeeraSaveShopButton');
    if (!tpl || !tpl.content) return false;
    box.appendChild(tpl.content.cloneNode(true));
    reviveScripts(box);
    box.addEventListener('click', onClick, true);
    return true;
  }

  function buildSheet() {
    sheet = document.createElement('div');
    sheet.className = 'meera-save-sheet';
    sheet.hidden = true;
    sheet.innerHTML =
      '<div class="meera-save-sheet__panel" role="dialog" aria-modal="true" aria-label="Save for later">' +
        '<button type="button" class="meera-save-sheet__close" aria-label="Close">&times;</button>' +
        '<div class="meera-save-sheet__product"><img alt="" width="80" height="80"><p></p></div>' +
        '<p class="meera-save-sheet__text">Sign in to keep it with your account, or we\'ll send it to your inbox.</p>' +
        '<label class="meera-save-sheet__consent"><input type="checkbox" name="subscribe" value="1">' +
        '<span>Also send me new arrivals and offers from Meera Capsule.</span></label>' +
        '<div class="meera-save-sheet__shop"></div>' +
        '<div class="meera-save-sheet__or" aria-hidden="true"><span>or</span></div>' +
        '<form class="meera-save-sheet__form" novalidate>' +
          emailRowHtml('Send') +
          '<p class="meera-save-sheet__error" role="alert" hidden></p>' +
        '</form>' +
        '<p class="meera-save-sheet__done" role="status" hidden></p>' +
      '</div>';
    document.body.appendChild(sheet);

    // Remember what they were saving (and the opt-in); completes once they're signed in.
    hasShop = addShopButton(sheet.querySelector('.meera-save-sheet__shop'), function () {
      var p = { mode: sheetMode, subscribe: wantsNews() };
      if (sheetMode === 'item') p.item = currentItem();
      ss(PENDING_KEY, JSON.stringify(p));
    });

    sheet.addEventListener('click', function (e) { if (e.target === sheet) closeSheet(); });
    sheet.querySelector('.meera-save-sheet__close').addEventListener('click', closeSheet);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && sheet && !sheet.hidden) closeSheet(); });

    sheet.querySelector('form').addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var form = e.currentTarget;
      var email = (form.elements.email.value || '').trim();
      var subscribe = wantsNews();
      var mode = sheetMode;
      var err = form.querySelector('.meera-save-sheet__error');
      var btn = form.querySelector('button[type="submit"]');
      if (!EMAIL_RE.test(email)) { err.textContent = 'Enter a valid email address.'; err.hidden = false; return; }
      err.hidden = true;
      btn.disabled = true;
      var save = mode === 'item' ? sendSavedItem(currentItem(), 'email', email) : sendSavedCart('email', true, email);
      Promise.resolve(save)
        .then(function () {
          // The save is done; the opt-in runs alongside and can't undo it.
          if (subscribe) subscribeEmail(email, mode === 'item' ? 'Product page: save for later' : 'Cart: save cart');
          ss(EMAIL_KEY, email);
          markKnown();
          softIdentify({ email: email });
          ['.meera-save-sheet__text', '.meera-save-sheet__consent', '.meera-save-sheet__shop', '.meera-save-sheet__or', 'form'].forEach(function (sel) {
            var n = sheet.querySelector(sel); if (n) n.hidden = true;
          });
          var done = sheet.querySelector('.meera-save-sheet__done');
          done.textContent = 'Saved.';
          done.hidden = false;
          if (mode === 'item') { rememberSaved(product.id); showSaved(); }
          else markCartSaved();
        })
        .catch(function () {
          btn.disabled = false;
          err.textContent = 'Something went wrong. Please try again.';
          err.hidden = false;
        });
    });
  }

  function resetSheet() {
    ['.meera-save-sheet__text', '.meera-save-sheet__consent', 'form'].forEach(function (sel) {
      var n = sheet.querySelector(sel); if (n) n.hidden = false;
    });
    sheet.querySelector('.meera-save-sheet__shop').hidden = !hasShop;
    sheet.querySelector('.meera-save-sheet__or').hidden = !hasShop;
    sheet.querySelector('.meera-save-sheet__done').hidden = true;
    sheet.querySelector('.meera-save-sheet__error').hidden = true;
    sheet.querySelector('.meera-save-sheet__button').disabled = false;
  }

  function setSheetProduct(imageUrl, label) {
    var img = sheet.querySelector('.meera-save-sheet__product img');
    if (imageUrl) { img.src = absUrl(imageUrl); img.hidden = false; } else { img.removeAttribute('src'); img.hidden = true; }
    sheet.querySelector('.meera-save-sheet__product p').textContent = label;
  }

  function openSheet(mode) {
    if (!sheet) buildSheet();
    resetSheet();
    sheetMode = mode;
    if (mode === 'item') {
      var item = currentItem();
      setSheetProduct(item.ImageURL, item.ProductName + (item.VariantName ? ' · ' + item.VariantName : ''));
    } else {
      setSheetProduct(null, 'Your cart');
      getCart().then(function (cart) {
        var paid = cart.items.filter(function (i) { return i.product_type !== 'Sample'; });
        var first = paid[0] || cart.items[0];
        var n = paid.reduce(function (s, i) { return s + i.quantity; }, 0) || cart.item_count;
        setSheetProduct(first ? first.image : null, 'Your cart · ' + n + (n === 1 ? ' item' : ' items'));
      });
    }
    lastFocus = document.activeElement;
    releaseDrawerFocus();
    sheet.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    sheet.querySelector('.meera-save-sheet__close').focus();
  }

  function closeSheet() {
    if (!sheet) return;
    sheet.hidden = true;
    document.documentElement.style.overflow = '';
    restoreDrawerFocus();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function buildSaveButton() {
    product = readProduct();
    if (!product) return;
    var anchor = document.querySelector('.product__info-container .buy-buttons');
    if (!anchor || document.querySelector('.meera-save-item')) return;
    var wrap = document.createElement('div');
    wrap.className = 'meera-save-item';
    wrap.innerHTML = '<button type="button" class="meera-save-item__trigger" aria-pressed="false">' + ICON +
      '<span>Save for later</span></button><p class="meera-save-item__status" role="status" hidden></p>';
    anchor.insertAdjacentElement('afterend', wrap);
    trigger = wrap.querySelector('button');
    statusEl = wrap.querySelector('.meera-save-item__status');
    if (savedIds().indexOf(product.id) !== -1) showSaved();

    trigger.addEventListener('click', function () {
      if (trigger.getAttribute('aria-pressed') === 'true') return;
      if (known) {
        trigger.disabled = true;
        sendSavedItem(currentItem(), sourceName()).then(function () {
          trigger.disabled = false;
          rememberSaved(product.id);
          showSaved();
        }, function () { trigger.disabled = false; openSheet('item'); });
        return;
      }
      openSheet('item');
    });
  }

  // "Save cart" in the cart drawer, directly under the Capsule progress bar (inside the bar's
  // block, so the bar's spacing still separates it from the item list). If the bar isn't
  // rendered, it sits under the drawer heading. Only when the cart has items (the footer
  // actions exist); re-added after each drawer rebuild.
  function injectCartTrigger() {
    document.querySelectorAll('cart-drawer').forEach(function (drawer) {
      if (drawer.querySelector('.meera-save-bag-link')) return;
      if (!drawer.querySelector('.drawer__footer-actions')) return;
      var bar = drawer.querySelector('.capsule-builder-bar');
      var heading = drawer.querySelector('.drawer__heading');
      if (!bar && !heading) return;
      var wrap = document.createElement('div');
      wrap.className = 'meera-save-bag-link';
      wrap.innerHTML = '<button type="button" class="meera-save-item__trigger" aria-pressed="false">' + ICON +
        '<span>Save cart</span></button>';
      if (bar) bar.appendChild(wrap);
      else heading.insertAdjacentElement('afterend', wrap);
      var btn = wrap.querySelector('button');
      if (ss(CART_SAVED_KEY)) { btn.setAttribute('aria-pressed', 'true'); btn.querySelector('span').textContent = 'Cart saved'; }
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (btn.getAttribute('aria-pressed') === 'true') return;
        if (known) {
          btn.disabled = true;
          sendSavedCart(sourceName(), true).then(
            function () { btn.disabled = false; markCartSaved(); },
            function () { btn.disabled = false; openSheet('cart'); }
          );
          return;
        }
        openSheet('cart');
      });
    });
  }

  /* ---------- 4. Empty cart drawer: sign in / sign up ---------- */

  function signupDone(box) {
    ['.meera-cart-signup__consent', '.meera-cart-signup__shop', '.meera-save-sheet__or', 'form'].forEach(function (sel) {
      var n = box.querySelector(sel); if (n) n.hidden = true;
    });
    var done = box.querySelector('.meera-cart-signup__done');
    done.textContent = 'You\'re signed up.';
    done.hidden = false;
  }

  // Signed-out visitors only (signed-in customers keep the theme's buttons). Inserted before
  // the theme's empty-cart buttons, which CSS then hides; re-added after each drawer rebuild.
  function injectEmptySignup() {
    if (customerEmail()) return;
    document.querySelectorAll('cart-drawer').forEach(function (drawer) {
      var actions = drawer.querySelector('.cart-page__empty-actions');
      if (!actions || drawer.querySelector('.meera-cart-signup')) return;
      var box = document.createElement('div');
      box.className = 'meera-cart-signup';
      box.innerHTML =
        '<label class="meera-cart-signup__consent"><input type="checkbox" name="subscribe" value="1">' +
        '<span>Send me new arrivals and offers from Meera Capsule.</span></label>' +
        '<div class="meera-cart-signup__shop"></div>' +
        '<div class="meera-save-sheet__or" aria-hidden="true"><span>or</span></div>' +
        '<form class="meera-cart-signup__form" novalidate>' +
          emailRowHtml('Sign up') +
          '<p class="meera-cart-signup__error" role="alert" hidden></p>' +
        '</form>' +
        '<p class="meera-cart-signup__done" role="status" hidden></p>';
      actions.insertAdjacentElement('beforebegin', box);

      var consent = box.querySelector('input[name="subscribe"]');
      var hasButton = addShopButton(box.querySelector('.meera-cart-signup__shop'), function () {
        ss(PENDING_KEY, JSON.stringify({ mode: 'signup', subscribe: consent.checked }));
      });
      if (!hasButton) {
        box.querySelector('.meera-cart-signup__shop').hidden = true;
        box.querySelector('.meera-save-sheet__or').hidden = true;
      }
      if (ss(SIGNUP_KEY)) { signupDone(box); return; }

      var form = box.querySelector('form');
      var err = box.querySelector('.meera-cart-signup__error');
      var btn = form.querySelector('button[type="submit"]');
      function fail(msg) { err.textContent = msg; err.hidden = false; }
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var email = (form.elements.email.value || '').trim();
        if (!EMAIL_RE.test(email)) { fail('Enter a valid email address.'); return; }
        if (!consent.checked) { fail('Tick the box above to sign up.'); return; }
        err.hidden = true;
        btn.disabled = true;
        subscribeEmail(email, 'Cart drawer, empty: sign up', true).then(function () {
          ss(EMAIL_KEY, email);
          ss(SIGNUP_KEY, '1');
          markKnown();
          softIdentify({ email: email });
          signupDone(box);
        }, function () {
          btn.disabled = false;
          fail('Something went wrong. Please try again.');
        });
      });
    });
  }

  /* ---------- drawer rebuilds ---------- */

  function onRender() {
    injectCartTrigger();
    injectEmptySignup();
    var markers = document.querySelectorAll('[data-meera-bag-marker]:not([data-meera-seen])');
    if (!markers.length) return;
    markers.forEach(function (m) { m.setAttribute('data-meera-seen', '1'); });
    if (known) sendSavedCart(sourceName(), false, null, false).catch(function () {});
  }

  var queued = false;
  function scheduleRender() {
    if (queued) return;
    queued = true;
    setTimeout(function () { queued = false; onRender(); }, 60);
  }

  /* ---------- init ---------- */

  function init() {
    document.querySelectorAll('[data-meera-bag-marker]').forEach(function (m) { m.setAttribute('data-meera-seen', '1'); });
    identifyCustomer();
    checkKnownVisitor();
    buildSaveButton();
    injectCartTrigger();
    injectEmptySignup();
    new MutationObserver(scheduleRender).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
